<template>
    <div class="d-contents">
        <v-row
            align-center
            :wrap="$vuetify.breakpoint.xsOnly"
            no-gutters
            justify-space-between
            class="flex-grow-0 mb-3"
            :class="{'nowrap': $vuetify.breakpoint.mdAndUp, 'px-3': $vuetify.breakpoint.smAndDown}">
            <v-col cols="auto">
                <v-btn text block
                   @click="emitFetch">
                    <v-icon left size="20">{{ ICONS.REFRESH }}</v-icon>
                    {{ $t('buttons.refresh_list') }}
                </v-btn>
            </v-col>
        </v-row>

        <Pagination :pagination="pagination"
                    @change-page="changePage($event)"
                    @change-amount="changeAmount($event)"></Pagination>

        <v-data-table
            :headers="headers"
            :loading="loader"
            disable-sort
            :items="sequenceEvaluations"
            disable-pagination
            hide-default-footer
            @dblclick:row="showSequenceEvaluationDetails"
            height="100%"
            fixed-header
            class="elevation-2 table-wrapper">
            <template v-for="(headerItem, i) in headers"
                      v-slot:[`header.${headerItem.value}`]="{ header }">
                <SortableHeader :header-item="header"
                                :key="i"
                                @sort="sort($event)"></SortableHeader>
            </template>
            <template v-slot:item.track_name="{ item }">
                <span class="clickable"
                      @click="showDetailsDialog(item)">{{ item.track_name }}</span>
            </template>
            <template v-slot:item.completion="{ item }">
                <span>{{ `${item.steps_completed_count} / ${item.steps_count}` }}</span>
            </template>
            <template v-slot:item.status="{ item }">
                <SequenceStatusChip :status="item.status" />
            </template>
            <template v-slot:item.start_datetime="{ item }">
                <span>{{ PARSE_DATETIME(item.start_datetime) }}</span>
            </template>
            <template v-slot:item.end_datetime="{ item }">
                <span>{{ PARSE_DATETIME(item.end_datetime) }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-row align="center"
                           justify="center">
                    <HintingIcon
                        class-name="mr-2"
                        :icon="ICONS.DETAILS"
                        data-test="showDetailsBtn"
                        @click="showDetailsDialog(item)"
                        :tooltip="$t('actions.view')"></HintingIcon>
                </v-row>
            </template>

        </v-data-table>

        <SequenceEvaluationsDetails :dialog="showDetails"
             @visibility="showDetails = $event"
             @close="closeDetailsDialog"
             v-if="activeSequenceEvaluation"></SequenceEvaluationsDetails>

    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { ICONS } from '@/constants/icons';
import SortableHeader from '@/components/widgets/tables/SortableHeader';
import HintingIcon from '@/components/widgets/HintingIcon';
import SequenceStatusChip from '@/components/cyclicRevisions/SequenceStatusChip.vue';
import paginationSettings from '@/mixins/paginationSettings';
import SequenceEvaluationsDetails from '@/components/cyclicRevisions/SequenceEvaluationsDetails.vue';
import Pagination from '@/components/widgets/tables/Pagination.vue';
import { PARSE_DATETIME } from '@/helpers/dates';

const SORTING_PARAMS = {
    track_name: 'track_name',
    customer_name: 'customer_name',
    location_name: 'location_name',
    point_name: 'point_name',
    assigned_employee_name: 'assigned_employee_name',
    status: 'status',
    start_datetime: 'start_datetime',
    end_datetime: 'end_datetime'
};

export default {
    name: 'SequenceEvaluationsTable',
    components: {
        SequenceEvaluationsDetails,
        SortableHeader,
        HintingIcon,
        SequenceStatusChip,
        Pagination
    },
    mixins: [paginationSettings],
    props: {
        loader: {
            type: Boolean,
            'default': false
        }
    },
    data: (vm) => ({
        pagination: vm.setupPagination(),
        ICONS,
        PARSE_DATETIME,
        sendMailVisibility: false,
        sortable: {
            track_name: 'desc',
            client_name: 'asc',
            location_name: 'asc',
            point_name: 'asc',
            assigned_employees_names: 'asc',
            status: 'asc',
            start_datetime: 'asc',
            end_datetime: 'asc'
        },
        showDetails: false
    }),
    computed: {
        ...mapState('cyclicRevisions', [
            'sequenceEvaluations',
            'sequenceEvaluationsTotalAmount'
        ]),
        ...mapGetters('manageSequenceEvaluation', [
            'activeSequenceEvaluation'
        ]),
        headers () {
            return [
                {
                    text: this.$t('incidents.table.headers.seq'),
                    value: 'index',
                    icon: false,
                    sortable: false
                },
                {
                    text: this.$t('tracks.sequence_evaluations.track_name'),
                    value: 'track_name',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.customer_name'),
                    value: 'customer_name',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.object'),
                    value: 'location_name',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.location'),
                    value: 'point_name',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.assigned_employees_names'),
                    value: 'assigned_employee_name',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.status'),
                    value: 'status',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.completion'),
                    value: 'completion',
                    sortable: false
                },
                {
                    text: this.$t('tracks.sequence_evaluations.start_datetime'),
                    value: 'start_datetime',
                    sortable: true
                },
                {
                    text: this.$t('tracks.sequence_evaluations.end_datetime'),
                    value: 'end_datetime',
                    sortable: true
                },
                {
                    text: this.$t('labels.actions'),
                    value: 'actions',
                    icon: false,
                    width: '200px',
                    sortable: false
                }
            ];
        }
    },
    methods: {
        emitFetch () {
            this.$emit('fetch');
        },
        changePage (event) {
            this.pagination.page = event;
            this.$emit('change-page', event);
        },
        changeAmount (event) {
            this.pagination.amount = event;
            this.$emit('change-amount', event);
        },
        sort (event) {
            const val = event;
            switch (this.sortable[val]) {
            case null:
                this.sortable[val] = 'asc';
                break;
            case 'asc':
                this.sortable[val] = 'desc';
                break;
            case 'desc':
                this.sortable[val] = 'asc';
                break;
            }

            this.$emit('sort', {
                name: SORTING_PARAMS[val],
                value: this.sortable[val]
            });
        },
        showSequenceEvaluationDetails (event, { item }) {
            this.showDetailsDialog(item);
        },
        showDetailsDialog (item) {
            this.$store.dispatch('manageSequenceEvaluation/getActiveSequenceEvaluation', item.id)
                .then(() => {
                    this.showDetails = true;
                });
        },
        closeDetailsDialog () {
            this.$store.commit('manageSequenceEvaluation/RESET_SEQUENCE_EVALUATION_DATA');
            this.showDetails = false;
        }
    },
    watch: {
        sequenceEvaluationsTotalAmount: {
            handler (val) {
                this.pagination.amountAll = val;
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">
    @import "@/assets/css/_variables";

    .table-wrapper {
        overflow: hidden;
    }

    .status {
        background-color: $tertiary;
        width: 15px;
        height: 15px;
        display: inline-block;
        border-radius: 50%;
        margin-top: 4px;
        margin-right: 4px;
    }

    .status--in_progress {
        background-color: $yellow;
    }

    .status--missed {
        background-color: $red;
    }

    .status--completed {
        background-color: $green;
    }

    .status--pending {
        background-color: $secondary;
    }
</style>
